import React, { useState, useEffect, useRef } from "react";
import { 
  Container, 
  Typography, 
  Box, 
  Stepper, 
  Step, 
  StepLabel, 
  StepContent, 
  useMediaQuery,
  Card,
  CardContent,
  Grid,
  Button,
  Divider,
  IconButton
} from "@mui/material";
import { styled, useTheme } from '@mui/material/styles';
import PersonAddIcon from '@mui/icons-material/PersonAdd';
import AccountBalanceWalletIcon from '@mui/icons-material/AccountBalanceWallet';
import PaymentsIcon from '@mui/icons-material/Payments';
import ArrowDownwardIcon from '@mui/icons-material/ArrowDownward';
import ArrowBackIosNewIcon from '@mui/icons-material/ArrowBackIosNew';
import ArrowForwardIosIcon from '@mui/icons-material/ArrowForwardIos';

// Custom styled components
const StepIcon = styled(Box)(({ theme, color }) => ({
  width: 80,
  height: 80,
  borderRadius: '50%',
  background: `linear-gradient(135deg, ${color} 0%, ${color}99 100%)`,
  display: 'flex',
  justifyContent: 'center',
  alignItems: 'center',
  boxShadow: `0 4px 20px 0 ${color}80`,
  margin: '0 auto',
  marginBottom: theme.spacing(3),
  position: 'relative',
  '&::after': {
    content: '""',
    position: 'absolute',
    width: '100%',
    height: '100%',
    borderRadius: '50%',
    background: `linear-gradient(135deg, ${color}66 0%, transparent 70%)`,
    animation: 'pulse 2s infinite',
  },
  '@keyframes pulse': {
    '0%': {
      transform: 'scale(1)',
      opacity: 0.7,
    },
    '50%': {
      transform: 'scale(1.1)',
      opacity: 0.3,
    },
    '100%': {
      transform: 'scale(1)',
      opacity: 0.7,
    },
  },
}));

const StepCard = styled(Card)(({ theme, active }) => ({
  height: '100%',
  borderRadius: theme.spacing(2),
  transition: 'all 0.5s cubic-bezier(0.34, 1.56, 0.64, 1)',
  overflow: 'visible',
  transform: active ? 'translateY(-15px) scale(1.03)' : 'none',
  boxShadow: active 
    ? '0 20px 30px -10px rgba(0, 0, 0, 0.4), 0 0 15px rgba(255, 255, 255, 0.1)' 
    : '0 10px 20px -5px rgba(0, 0, 0, 0.3), 0 0 10px rgba(255, 255, 255, 0.05)',
  background: active 
    ? 'linear-gradient(135deg, #1a1a2e 0%, #16213e 100%)' 
    : 'linear-gradient(135deg, #0f0f1a 0%, #121728 100%)',
  border: active 
    ? '1px solid rgba(255, 255, 255, 0.1)' 
    : '1px solid rgba(255, 255, 255, 0.05)',
  backdropFilter: 'blur(10px)',
  '&:hover': {
    transform: 'translateY(-15px) scale(1.03)',
    boxShadow: '0 20px 30px -10px rgba(0, 0, 0, 0.4), 0 0 15px rgba(255, 255, 255, 0.1)',
    border: '1px solid rgba(255, 255, 255, 0.15)',
  },
}));

const StepNumber = styled(Box)(({ theme, active, color }) => ({
  position: 'absolute',
  top: -20,
  left: -20,
  width: 40,
  height: 40,
  borderRadius: '50%',
  backgroundColor: '#0a0a15',
  border: `2px solid ${active ? color : 'rgba(255, 255, 255, 0.2)'}`,
  display: 'flex',
  justifyContent: 'center',
  alignItems: 'center',
  fontWeight: 'bold',
  fontSize: '1.2rem',
  color: active ? color : 'rgba(255, 255, 255, 0.6)',
  transition: 'all 0.3s ease',
  boxShadow: active 
    ? `0 0 10px ${color}80` 
    : '0 4px 8px rgba(0, 0, 0, 0.2)',
}));

const ArrowContainer = styled(Box)(({ theme }) => ({
  [theme.breakpoints.up('md')]: {
    display: 'flex',
    justifyContent: 'center',
    alignItems: 'center',
    padding: theme.spacing(2),
  },
  [theme.breakpoints.down('md')]: {
    display: 'flex',
    justifyContent: 'center',
    alignItems: 'center',
    padding: theme.spacing(1),
    transform: 'rotate(90deg)',
  },
}));

const SlideNavButton = styled(IconButton)(({ theme, direction }) => ({
  position: 'absolute',
  top: '50%',
  transform: 'translateY(-50%)',
  zIndex: 10,
  backgroundColor: 'rgba(10, 10, 25, 0.7)',
  color: 'white',
  backdropFilter: 'blur(5px)',
  boxShadow: '0 4px 15px rgba(0, 0, 0, 0.3)',
  border: '1px solid rgba(255, 255, 255, 0.1)',
  transition: 'all 0.3s ease',
  '&:hover': {
    backgroundColor: 'rgba(30, 30, 50, 0.9)',
    transform: direction === 'prev' 
      ? 'translateY(-50%) translateX(-3px)' 
      : 'translateY(-50%) translateX(3px)',
  },
  ...(direction === 'prev' ? { left: theme.spacing(2) } : { right: theme.spacing(2) }),
}));

const AnimatedButton = styled(Button)(({ theme, gradient }) => ({
  position: 'relative',
  overflow: 'hidden',
  transition: 'all 0.3s ease',
  padding: '10px 24px',
  background: gradient,
  boxShadow: '0 8px 16px rgba(0, 0, 0, 0.3)',
  border: '1px solid rgba(255, 255, 255, 0.1)',
  '&::before': {
    content: '""',
    position: 'absolute',
    top: '-50%',
    left: '-50%',
    width: '200%',
    height: '200%',
    background: 'radial-gradient(circle, rgba(255,255,255,0.3) 0%, transparent 70%)',
    opacity: 0,
    transition: 'opacity 0.5s ease',
  },
  '&:hover': {
    transform: 'translateY(-3px)',
    boxShadow: '0 12px 20px rgba(0, 0, 0, 0.4)',
    '&::before': {
      opacity: 1,
    },
  },
  '&:active': {
    transform: 'translateY(0)',
  },
}));

const BackgroundImage = styled(Box)(({ theme, index }) => ({
  position: 'absolute',
  top: 0,
  left: 0,
  width: '100%',
  height: '100%',
  opacity: 0.08,
  backgroundSize: 'cover',
  backgroundPosition: 'center',
  transition: 'opacity 0.5s ease-in-out',
  backgroundImage: getBackgroundImage(index),
  zIndex: 0,
}));

const FloatingParticle = styled(Box)(({ size, delay, speed, color }) => ({
  position: 'absolute',
  width: size,
  height: size,
  background: color,
  borderRadius: '50%',
  filter: 'blur(2px)',
  opacity: 0.3,
  animation: `float ${speed}s infinite ease-in-out ${delay}s`,
  '@keyframes float': {
    '0%, 100%': {
      transform: 'translateY(0) translateX(0)',
    },
    '50%': {
      transform: 'translateY(-30px) translateX(20px)',
    },
  },
}));

// Background images for each step
function getBackgroundImage(index) {
  const images = [
    'url("/api/placeholder/1200/800")',
    'url("/api/placeholder/1200/800")',
    'url("/api/placeholder/1200/800")'
  ];
  return images[index % images.length];
}

// Intersection Observer for scroll animations
function useIntersectionObserver(options) {
  const ref = useRef(null);
  const [isVisible, setIsVisible] = useState(false);

  useEffect(() => {
    const observer = new IntersectionObserver(([entry]) => {
      setIsVisible(entry.isIntersecting);
    }, options);

    if (ref.current) {
      observer.observe(ref.current);
    }

    return () => {
      if (ref.current) {
        observer.unobserve(ref.current);
      }
    };
  }, [ref, options]);

  return [ref, isVisible];
}

const HowItWorkPage = () => {
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('md'));
  const [activeStep, setActiveStep] = useState(0);
  const [isAnimating, setIsAnimating] = useState(false);
  const [cardsRef, cardsVisible] = useIntersectionObserver({
    threshold: 0.2,
    triggerOnce: false
  });
  
  // Auto rotation effect
  useEffect(() => {
    const interval = setInterval(() => {
      if (!isAnimating) {
        setActiveStep((prevStep) => (prevStep + 1) % steps.length);
      }
    }, 5000);
    
    return () => clearInterval(interval);
  }, [isAnimating]);

  const handleNext = () => {
    setIsAnimating(true);
    setActiveStep((prevStep) => (prevStep + 1) % steps.length);
    setTimeout(() => setIsAnimating(false), 500);
  };

  const handlePrev = () => {
    setIsAnimating(true);
    setActiveStep((prevStep) => (prevStep - 1 + steps.length) % steps.length);
    setTimeout(() => setIsAnimating(false), 500);
  };
  
  const steps = [
    {
      title: "Create Your Account",
      description: "Sign up in minutes with our streamlined verification process. We've eliminated unnecessary steps to get you started quickly while maintaining top-tier security.",
      icon: <PersonAddIcon style={{ fontSize: 40, color: "#fff" }} />,
      color: "#7928ca", // vibrant purple
      buttonText: "Join Now",
      gradient: "linear-gradient(135deg, #7928ca 0%, #ff0080 100%)"
    },
    {
      title: "Connect Your Finances",
      description: "Link multiple payment sources with our bank-grade encryption. Choose between instant transfers, scheduled payments, or maintaining a prepaid balance.",
      icon: <AccountBalanceWalletIcon style={{ fontSize: 40, color: "#fff" }} />,
      color: "#0070f3", // vibrant blue
      buttonText: "Connect",
      gradient: "linear-gradient(135deg, #0070f3 0%, #00cffd 100%)"
    },
    {
      title: "Seamless Transactions",
      description: "Experience frictionless payments with smart recommendations based on your habits. Get detailed insights and custom reports on your spending patterns.",
      icon: <PaymentsIcon style={{ fontSize: 40, color: "#fff" }} />,
      color: "#00c853", // vibrant green
      buttonText: "Start Using",
      gradient: "linear-gradient(135deg, #00c853 0%, #69f0ae 100%)"
    }
  ];

  // Generate random particles for background effect
  const particles = [...Array(15)].map((_, i) => ({
    id: i,
    top: `${Math.random() * 100}%`,
    left: `${Math.random() * 100}%`,
    size: `${Math.random() * 6 + 2}px`,
    delay: Math.random() * 2,
    speed: Math.random() * 10 + 10,
    color: steps[Math.floor(Math.random() * steps.length)].color
  }));

  return (
    <Box 
      sx={{ 
        py: { xs: 8, md: 12 },
        position: 'relative',
        overflow: 'hidden',
        background: 'linear-gradient(135deg, #050510 0%, #0a0a1a 100%)',
        minHeight: '100vh',
        color: 'white',
      }}
    >
      {/* Floating particles */}
      {particles.map(particle => (
        <FloatingParticle 
          key={particle.id}
          size={particle.size}
          delay={particle.delay}
          speed={particle.speed}
          color={particle.color}
          sx={{ top: particle.top, left: particle.left }}
        />
      ))}
      
      {/* Dynamic background image */}
      <BackgroundImage index={activeStep} />
      
      {/* Animated gradient background */}
      <Box sx={{
        position: 'absolute',
        top: 0,
        right: 0,
        width: '100%',
        height: '100%',
        background: steps[activeStep].gradient,
        opacity: 0.07,
        zIndex: 0,
        transition: 'background 0.5s ease',
      }} />
      
      {/* Radial light effect */}
      <Box sx={{
        position: 'absolute',
        bottom: 0,
        left: 0,
        width: '100%',
        height: '40%',
        background: 'radial-gradient(circle at bottom center, rgba(255,255,255,0.1) 0%, rgba(255,255,255,0) 70%)',
        zIndex: 0,
        filter: 'blur(5px)',
      }} />

      <Container maxWidth="lg" sx={{ position: 'relative', zIndex: 1 }}>
        <Box 
          sx={{ 
            textAlign: 'center', 
            mb: { xs: 6, md: 8 },
            opacity: cardsVisible ? 1 : 0,
            transform: cardsVisible ? 'translateY(0)' : 'translateY(30px)',
            transition: 'opacity 0.6s ease, transform 0.6s ease',
          }}
          ref={cardsRef}
        >
          <Typography 
            variant="h2" 
            fontWeight="700" 
            sx={{ 
              mb: 2,
              fontSize: { xs: '2rem', md: '3.5rem' },
              background: 'linear-gradient(90deg, #fff 0%, #aaa 100%)',
              backgroundClip: 'text',
              WebkitBackgroundClip: 'text',
              color: 'transparent',
              textShadow: '0px 4px 12px rgba(0,0,0,0.2)',
              position: 'relative',
              display: 'inline-block',
              '&::after': {
                content: '""',
                position: 'absolute',
                bottom: '-10px',
                left: '50%',
                transform: 'translateX(-50%)',
                width: '60px',
                height: '4px',
                background: steps[activeStep].gradient,
                borderRadius: '2px',
                transition: 'background 0.5s ease',
              }
            }}
          >
            How It Works
          </Typography>
          <Typography 
            variant="h6" 
            color="text.secondary"
            sx={{ 
              maxWidth: 700, 
              mx: 'auto',
              fontSize: { xs: '1rem', md: '1.25rem' },
              opacity: 0.7,
              mt: 4,
            }}
          >
            Experience our revolutionary payment platform in three simple steps
          </Typography>
        </Box>

        {isMobile ? (
          // Mobile view - vertical stepper with active state
          <Box 
            sx={{ 
              position: 'relative', 
              mt: 4,
              opacity: cardsVisible ? 1 : 0,
              transform: cardsVisible ? 'translateY(0)' : 'translateY(30px)',
              transition: 'opacity 0.6s ease, transform 0.6s ease',
            }}
            ref={cardsRef}
          >
            {!isAnimating && (
              <>
                <SlideNavButton direction="prev" onClick={handlePrev} size="small">
                  <ArrowBackIosNewIcon fontSize="small" />
                </SlideNavButton>
                <SlideNavButton direction="next" onClick={handleNext} size="small">
                  <ArrowForwardIosIcon fontSize="small" />
                </SlideNavButton>
              </>
            )}
            
            <Stepper orientation="vertical" activeStep={activeStep} sx={{ mt: 4 }}>
              {steps.map((step, index) => (
                <Step 
                  key={index} 
                  active={activeStep === index} 
                  completed={index < activeStep}
                  sx={{
                    '.MuiStepLabel-root': {
                      padding: '16px',
                      borderRadius: '8px',
                      background: activeStep === index 
                        ? 'rgba(255, 255, 255, 0.05)' 
                        : 'transparent',
                      border: activeStep === index 
                        ? '1px solid rgba(255, 255, 255, 0.1)' 
                        : '1px solid transparent',
                      transition: 'all 0.3s ease',
                      backdropFilter: activeStep === index ? 'blur(5px)' : 'none',
                    }
                  }}
                >
                  <StepLabel
                    StepIconComponent={() => (
                      <StepIcon color={step.color}>
                        {step.icon}
                      </StepIcon>
                    )}
                  >
                    <Typography variant="h5" fontWeight="600" sx={{ mb: 1, color: 'white' }}>
                      {step.title}
                    </Typography>
                  </StepLabel>
                  <StepContent sx={{ borderLeft: `2px solid ${step.color}` }}>
                    <Box sx={{ mb: 4, pl: 1 }}>
                      <Typography variant="body1" paragraph sx={{ color: 'rgba(255, 255, 255, 0.7)' }}>
                        {step.description}
                      </Typography>
                      <AnimatedButton 
                        variant="contained" 
                        gradient={step.gradient}
                        sx={{ mt: 2 }}
                      >
                        {step.buttonText}
                      </AnimatedButton>
                    </Box>
                  </StepContent>
                </Step>
              ))}
            </Stepper>
          </Box>
        ) : (
          // Desktop view - horizontal cards with arrows and sliding
          <Box 
            sx={{ 
              position: 'relative',
              opacity: cardsVisible ? 1 : 0,
              transform: cardsVisible ? 'translateY(0)' : 'translateY(30px)',
              transition: 'opacity 0.8s ease, transform 0.8s ease',
            }}
            ref={cardsRef}
          >
            {!isAnimating && (
              <>
                <SlideNavButton direction="prev" onClick={handlePrev}>
                  <ArrowBackIosNewIcon />
                </SlideNavButton>
                <SlideNavButton direction="next" onClick={handleNext}>
                  <ArrowForwardIosIcon />
                </SlideNavButton>
              </>
            )}
            
            <Grid container spacing={3} alignItems="center">
              {steps.map((step, index) => (
                <React.Fragment key={index}>
                  <Grid item xs={12} md={3.5}>
                    <Box 
                      sx={{ 
                        position: 'relative', 
                        transition: 'all 0.5s cubic-bezier(0.34, 1.56, 0.64, 1)', 
                        opacity: isAnimating ? 0.7 : 1,
                        transform: `translateY(${index * 15}px) scale(${activeStep === index ? 1 : 0.98})`,
                        transitionDelay: `${index * 0.1}s`,
                      }}
                    >
                      <StepNumber active={index === activeStep} color={step.color}>{index + 1}</StepNumber>
                      <StepCard elevation={4} active={index === activeStep}>
                        <CardContent 
                          sx={{ 
                            pt: 4, 
                            pb: 4, 
                            textAlign: 'center',
                            position: 'relative',
                            zIndex: 1,
                            overflow: 'hidden',
                            '&::before': activeStep === index ? {
                              content: '""',
                              position: 'absolute',
                              top: 0,
                              left: 0,
                              width: '100%',
                              height: '100%',
                              background: `radial-gradient(circle at center, ${step.color}26 0%, transparent 70%)`,
                              zIndex: -1,
                            } : {},
                          }}
                        >
                          <StepIcon color={step.color}>
                            {step.icon}
                          </StepIcon>
                          <Typography variant="h5" fontWeight="600" gutterBottom sx={{ color: 'white' }}>
                            {step.title}
                          </Typography>
                          <Typography variant="body1" sx={{ mb: 3, color: 'rgba(255, 255, 255, 0.7)' }}>
                            {step.description}
                          </Typography>
                          <Divider sx={{ 
                            my: 2, 
                            borderColor: 'rgba(255, 255, 255, 0.1)',
                            '&::before, &::after': {
                              borderColor: 'rgba(255, 255, 255, 0.1)',
                            }
                          }} />
                          <AnimatedButton 
                            variant="contained" 
                            gradient={step.gradient}
                            sx={{ mt: 2 }}
                          >
                            {step.buttonText}
                          </AnimatedButton>
                        </CardContent>
                      </StepCard>
                    </Box>
                  </Grid>
                  
                  {index < steps.length - 1 && (
                    <Grid item xs={12} md={0.5}>
                      <ArrowContainer>
                        <ArrowDownwardIcon 
                          sx={{ 
                            transform: { md: 'rotate(0deg)', xs: 'rotate(90deg)' },
                            color: index === activeStep ? steps[index].color : 'rgba(255, 255, 255, 0.3)',
                            opacity: 0.7,
                            transition: 'all 0.3s ease',
                          }} 
                          fontSize="large"
                        />
                      </ArrowContainer>
                    </Grid>
                  )}
                </React.Fragment>
              ))}
            </Grid>
            
            {/* Step indicators */}
            <Box sx={{ display: 'flex', justifyContent: 'center', mt: 6 }}>
              {steps.map((step, index) => (
                <Box 
                  key={index}
                  sx={{
                    width: 12,
                    height: 12,
                    borderRadius: '50%',
                    backgroundColor: index === activeStep ? step.color : 'rgba(255, 255, 255, 0.1)',
                    mx: 0.5,
                    transition: 'all 0.3s ease',
                    cursor: 'pointer',
                    position: 'relative',
                    '&::after': index === activeStep ? {
                      content: '""',
                      position: 'absolute',
                      top: '-4px',
                      left: '-4px',
                      width: 20,
                      height: 20,
                      borderRadius: '50%',
                      border: `1px solid ${step.color}`,
                      opacity: 0.5,
                      animation: 'pulse 2s infinite',
                    } : {},
                  }}
                  onClick={() => {
                    setIsAnimating(true);
                    setActiveStep(index);
                    setTimeout(() => setIsAnimating(false), 500);
                  }}
                />
              ))}
            </Box>
          </Box>
        )}
      </Container>
    </Box>
  );
};

export default HowItWorkPage;